import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { AlertTriangle, RotateCcw, Home } from 'lucide-react'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo)
    }
  }

  handleReset = () => {
    this.setState({ error: null })
  }

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="p-6 text-center space-y-4">
              <AlertTriangle className="w-12 h-12 text-error mx-auto" />
              <h1 className="text-xl font-semibold text-foreground">發生錯誤</h1>
              <p className="text-sm text-muted-foreground">
                頁面載入時發生未預期的錯誤，請重試或回到首頁。
              </p>
              {import.meta.env.DEV && (
                <pre className="text-left text-xs bg-secondary p-3 rounded-lg overflow-auto max-h-40 text-error">
                  {this.state.error.message}
                </pre>
              )}
              <div className="flex gap-2 justify-center">
                <Button onClick={this.handleReset} variant="outline" className="flex items-center gap-2">
                  <RotateCcw className="w-4 h-4" />
                  重試
                </Button>
                <Button
                  onClick={() => { window.location.href = '/' }}
                  className="flex items-center gap-2"
                >
                  <Home className="w-4 h-4" />
                  回到首頁
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}
